/**
 * Academy PEOPLE hooks (F3-D) — half REST, half local-first, and the
 * boundary is exactly the one in `docs/DATA_ACCESS_POLICY.md`.
 *
 * **Via REST (plan 06 · E4):** the directory, a student's record, a
 * teacher's profile, and the academy's guardian links. This is ANOTHER
 * person's data, with PII (national ID, phone, address) and a scope that
 * depends on role and campus — none of that fits in a flat `with:`, and the
 * attempt cost replicating 2,000 records to every admin's phone.
 *
 * **Local-first:** my enrollments, my certificates, my own record, my
 * profile and my wards (`my_cohorts_student`, `my_certificates`,
 * `my_membership_profiles`) and my role in the org (`organizations`).
 *
 * **Writing is entirely the server's.** The `PUT`s below invalidate the read
 * cache; there's no local optimism because there's no local row to update.
 */
import type { Accessor } from "solid-js";
import {
  academyPeopleQueryKeys,
  type DirectoryMemberRow,
  getMyGuardianWardsQuery,
  getMyStudentProfileQuery,
  getMyTeacherProfileQuery,
  getUserCertificatesQuery,
  getUserEnrollmentsQuery,
  type GuardianLinkRow,
  normalizeOrgRole,
  type OrgCapability,
  roleHasCapability,
  type StudentRecord,
  type StudentRecordPatch,
  type TeacherRecord,
  type TeacherRecordPatch,
} from "@space/shared";
import {
  createPowerSyncQuery,
  type CreatePowerSyncQueryResult,
  createPowerSyncSingle,
} from "@space/shared/solid";
import { useQueryClient } from "@tanstack/solid-query";
import {
  and,
  eq,
  type GuardianLink,
  member,
  type StudentProfile,
  type TeacherProfile,
} from "@space/powersync";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/**
 * An organization's directory (one row per member + summarized record),
 * **with no identity**: name/email/image are supplied by `useOrgIdentities`
 * and stitched together with `hydrateDirectory` (D3 · plan 06, E2).
 *
 * **Single path since E4.** Served by `GET /api/academy/:orgId/directory`,
 * which requires managing students or members and scopes `campus_admin` to
 * their campus — a scope sync rules can't express (`org_admin_scope` used to
 * replicate the WHOLE academy's records to any admin). The local branch and
 * its rollback flag died with `student_profile`'s cut: without those
 * replicated rows, the Drizzle query returned empty for everyone, so keeping
 * it would have been a switch that only turns the screen off.
 *
 * The endpoint does NOT return identity on purpose: if it did, there would
 * be a second place deciding who sees an email, which is the D3 defect.
 */
export function useOrgDirectory(
  orgId: Accessor<string | undefined>,
): CreatePowerSyncQueryResult<DirectoryMemberRow> {
  // `Error` and not `unknown`: the hook's public signature exposes
  // `error: Error | null` (the one it had with PowerSync), and
  // `handleApiResponse` already throws `Error`.
  const rest = createApiQuery<DirectoryMemberRow[], Error>(
    () => academyPeopleQueryKeys.directory(orgId()),
    async () =>
      api.GET("/api/academy/{orgId}/directory", {
        params: { path: { orgId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()), staleTime: OFFLINE_CACHE_STALE_TIME_MS }),
  );
  // The `CreatePowerSyncQueryResult` shape is kept so the screens don't
  // notice the transport change (same signature they had with the mirror).
  return {
    get data() {
      return rest.data;
    },
    get isFetching() {
      return rest.isFetching;
    },
    get isLoading() {
      return rest.isLoading;
    },
    get isPending() {
      return rest.isPending;
    },
    get error() {
      return rest.error;
    },
    get isError() {
      return rest.isError;
    },
    get status() {
      return rest.status;
    },
    refetch: () => {
      void rest.refetch();
    },
  };
}

/**
 * A student's record within the org
 * (`GET /api/academy/:orgId/students/:userId`).
 *
 * 404 = "there's no record **that you can see**": the server doesn't
 * distinguish between nonexistent and outside your campus on purpose, so as
 * not to confirm that a student from another campus exists. The screen
 * treats it as "no record" and offers to create one, same as it used to do
 * with the local mirror's empty `limit(1)`.
 */
export function useStudentRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  return createApiQuery<StudentRecord, unknown>(
    () => academyPeopleQueryKeys.studentProfile(orgId(), userId()),
    async () =>
      api.GET("/api/academy/{orgId}/students/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(orgId()) && Boolean(userId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
      // No record yet is NOT a transient failure: retrying only delays the
      // creation form the screen wants to show.
      retry: false,
    }),
  );
}

/** Saves (creates or updates) the record. SPARSE patch: what's absent isn't touched. */
export function useSaveStudentRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  const qc = useQueryClient();
  return createApiMutation<StudentRecordPatch, StudentRecord, unknown>(
    async (patch) =>
      api.PUT("/api/academy/{orgId}/students/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
        body: patch,
      }),
    () => ({
      onSuccess: () => {
        void qc.invalidateQueries({
          queryKey: academyPeopleQueryKeys.studentProfile(orgId(), userId()),
        });
        // The directory carries the record's national ID, status and campus:
        // if it isn't invalidated, the list keeps showing the old value until it expires.
        void qc.invalidateQueries({ queryKey: academyPeopleQueryKeys.directory(orgId()) });
      },
    }),
  );
}

/** Teacher record (`GET /api/academy/:orgId/teachers/:userId`). */
export function useTeacherRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  return createApiQuery<TeacherRecord, unknown>(
    () => academyPeopleQueryKeys.teacherProfile(orgId(), userId()),
    async () =>
      api.GET("/api/academy/{orgId}/teachers/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(orgId()) && Boolean(userId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
      retry: false,
    }),
  );
}

/** Saves (creates or updates) the teacher record. */
export function useSaveTeacherRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  const qc = useQueryClient();
  return createApiMutation<TeacherRecordPatch, TeacherRecord, unknown>(
    async (patch) =>
      api.PUT("/api/academy/{orgId}/teachers/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
        body: patch,
      }),
    () => ({
      onSuccess: () => {
        void qc.invalidateQueries({
          queryKey: academyPeopleQueryKeys.teacherProfile(orgId(), userId()),
        });
        void qc.invalidateQueries({ queryKey: academyPeopleQueryKeys.directory(orgId()) });
      },
    }),
  );
}

// D3 (plan 06 · E2): `useMemberIdentity` has been DELETED along with its
// Drizzle query. A person's identity is now served by `useOrgIdentities`
// (`./use-org-identities`), the client's single source and the only place
// where email may or may not arrive, per what the server decides by capability.

/** A user's enrollments (joined with the cohort). */
export function useUserEnrollments(userId: Accessor<string | undefined>) {
  const base = useBase();
  type Row = Awaited<ReturnType<ReturnType<typeof getUserEnrollmentsQuery>["execute"]>>[number];
  return createPowerSyncQuery<Row>({
    queryKey: () => academyPeopleQueryKeys.enrollments(userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = userId();
      return db && uid ? getUserEnrollmentsQuery(db, uid) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** A user's certificates (local; empty if not synced yet). */
export function useUserCertificates(userId: Accessor<string | undefined>) {
  const base = useBase();
  type Row = Awaited<ReturnType<ReturnType<typeof getUserCertificatesQuery>["execute"]>>[number];
  return createPowerSyncQuery<Row>({
    queryKey: () => academyPeopleQueryKeys.certificates(userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = userId();
      return db && uid ? getUserCertificatesQuery(db, uid) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * Organization's guardian links (for the record).
 *
 * E4: via REST (`GET /api/org/listGuardians`, which already existed and was
 * already the WRITE gate — `addGuardian`/`removeGuardian`). The local query
 * read `guardian_link` from `org_admin_scope`, i.e. every link in the
 * academy; the endpoint returns the same data already resolved with name
 * and email, so an identity join disappears too.
 */
export function useOrgGuardianLinks(orgId: Accessor<string | undefined>) {
  return createApiQuery<GuardianLinkRow[], unknown>(
    () => academyPeopleQueryKeys.guardianLinks(orgId()),
    async () =>
      api.GET("/api/org/listGuardians", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()), staleTime: OFFLINE_CACHE_STALE_TIME_MS }),
  );
}

// ─── self-view (student / guardian) ───────────────────────────────────────────

/** MY student record. */
export function useMyStudentProfile() {
  const base = useBase();
  return createPowerSyncQuery<StudentProfile>({
    queryKey: () => academyPeopleQueryKeys.myStudentProfile(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyStudentProfileQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MY teacher profile. */
export function useMyTeacherProfile() {
  const base = useBase();
  return createPowerSyncSingle<TeacherProfile>({
    queryKey: () => academyPeopleQueryKeys.myTeacherProfile(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyTeacherProfileQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MY wards as a guardian (guardian_link where I'm the guardian). */
export function useMyGuardianWards() {
  const base = useBase();
  return createPowerSyncQuery<GuardianLink>({
    queryKey: () => academyPeopleQueryKeys.myGuardianLinks(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyGuardianWardsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

// ─── current user's capability in the active org (UI gating) ─────────────────

/**
 * Current user's role in `orgId` (from `member`, synced via the
 * `organizations` stream). Returns capability helpers to gate editing in the
 * UI. The write-path always re-validates: this is cosmetic only.
 */
export function useMyOrgRole(orgId: Accessor<string | undefined>) {
  const base = useBase();
  type MemberRow = { role: string | null };
  const query = createPowerSyncSingle<MemberRow>({
    queryKey: () => academyPeopleQueryKeys.myRole(orgId(), base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const oid = orgId();
      const uid = base.userId();
      return db && oid && uid
        ? db
            .select({ role: member.role })
            .from(member)
            .where(and(eq(member.organization_id, oid), eq(member.user_id, uid)))
            .limit(1)
        : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });

  const role = () => normalizeOrgRole(query.data?.role);
  const can = (cap: OrgCapability) => roleHasCapability(query.data?.role, cap);
  return { role, can, isLoading: () => query.isLoading };
}
